import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import PulseLoader from "react-spinners/PulseLoader";
import { getDateFormat } from "@utils/datePickerUtils";
import {
  Dialog,
  Button,
  RadioButton,
  Image,
  CustomCheckBox,
  Anchor,
} from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import { handleApiError } from "@utils/errorHandler";
import closeIcon from "@icons/ic-close-white.svg";
import sentIcon from "@icons/ic-sent.svg";
import contactBlueIcon from "@icons/ic-contact-blue.svg";
import contactRedIcon from "@icons/ic-contact-red.svg";
import warningIcon from "@icons/ic-warning.svg";

import {
  HeaderWrapper,
  Title,
  FormBody,
  OuterWrapper,
  FlexRow,
  SectionContainer,
  ButtonWrapper,
  GridRow,
  GridSubItem,
  RadioItem,
  RowHeader,
  SolutionOuter,
  ProductContainer,
  ErrorDiv,
  RequiredMessage,
} from "./AddEditPlanningNotification.styled";
import "./AddEditPlanningNotification.scss";

const AddEditPlanningNotification = (props) => {
  const { t, onClose, planningDate, updateRefeshStatus, countryId } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [createPlanningAPIError, setCreatePlanningAPIError] = useState("");
  const [isPlanningCreating, setIsPlanningCreating] = useState(false);
  const [type, setType] = useState(0);
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [companySelected, setCompanySelected] = useState([]);
  const [totalContact, setTotalContact] = useState(0);
  const [selectedContact, setSelectedContact] = useState(0);
  const [noContact, setNoContact] = useState(0);
  const [formError, setFormError] = useState("");

  const fetchPlanningNotification = () => {
    setIsLoading(true);
    axios
      .get(
        `/suppliersPlanning/notifications?planningDate=${moment(
          planningDate).format("YYYY-MM-DD")}&type=${type}&countries=[${countryId}]`
      )
      .then((response) => {
        setData(response.data?.planingNotifications);
        setSelectAll(false);
        setCompanySelected([]);
        setTotalContact(0);
        setSelectedContact(0);
        setNoContact(0);
        setIsLoading(false);
      })
      .catch((error) => handleApiError(error));
  };

  useEffect(() => {
    fetchPlanningNotification();
  }, [type]);

  const onTypeChange = (e) => {
    setType(parseInt(e.target.value, 10));
  };

  const selectAllCompanies = (e) => {
    if (e.currentTarget.checked) {
      setSelectAll(true);
      const companyList = [];
      data.map((planning) => {
        companyList.push(parseInt(planning.id, 10));
      });
      setCompanySelected(companyList);
    } else {
      setSelectAll(false);
      setCompanySelected([]);
    }
    setFormError("");
  };

  const onCompanySelect = (e, id) => {
    if (companySelected.indexOf(id) === -1 && e.currentTarget.checked)
      companySelected.push(id);
    else {
      const index = companySelected.indexOf(id);
      if (index >= 0) {
        companySelected.splice(index, 1);
      }
    }
    setCompanySelected([...companySelected]);
    if (companySelected.length === data.length) {
      setSelectAll(true);
    } else setSelectAll(false);
    setFormError("");
  };

  const getContactDetails = () => {
    let selectedContactcount = 0;
    let totalContactCount = 0;
    let invalidContactCount = 0;
    data?.map((planning) => {
      if (companySelected.indexOf(planning.id) !== -1) {
        if (planning.contacts?.length > 0) {
          planning.contacts.map((contact) => {
            if (
              contact.email &&
              /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(contact.email)
            ) {
              selectedContactcount += 1;
            } else {
              invalidContactCount += 1;
            }
          });
        } else {
          invalidContactCount += 1;
        }
      }
      if (planning.contacts?.length > 0) {
        totalContactCount += planning.contacts?.length;
      }
    });
    setSelectedContact(selectedContactcount);
    setTotalContact(totalContactCount);
    setNoContact(invalidContactCount);
  };

  useEffect(() => {
    getContactDetails();
  }, [companySelected]);

  const validateForm = () => {
    let isFormValid = true;
    if (companySelected?.length === 0) {
      isFormValid = false;
      setFormError("COMPANY_REQUIRED");
    }
    return isFormValid;
  };

  const getFormData = () => {
    const formData = {
      planningDate: moment(planningDate).format("YYYY-MM-DD"),
      type,
      selectedCompanies: companySelected,
    };
    return formData;
  };

  const onSubmit = () => {
    if (isPlanningCreating) {
      return false;
    }
    const isFormValid = validateForm();
    if (isFormValid) {
      setIsPlanningCreating(true);
      setCreatePlanningAPIError("");
      axios
        .post(`/suppliersPlanning/notifications`, getFormData())
        .then((response) => {
          setIsPlanningCreating(false);
          updateRefeshStatus();
          onClose();
        })
        .catch((error) => {
          setIsPlanningCreating(false);
          setCreatePlanningAPIError(error.response?.data?.message ?? "");
        });
    }
  };

  return (
    <Dialog width="65rem">
      <div className="add-edit-planning-notification">
        <HeaderWrapper>
          <Title>
            {t("SUPPLIERS_PLANNING_NOTIFICATION_TITLE")}
            {" - "}
            {planningDate
              ? getDateFormat(planningDate,"YYYY-MM-DD")
              : planningDate}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Enquiry price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OuterWrapper>
            <FlexRow>
              <SectionContainer>
                {isLoading && (
                  <div className="suggestions-list-loading">
                    <PulseLoader color="#1bace4" size={15} />
                  </div>
                )}
                {!isLoading && (
                  <div>
                    <GridRow column="1fr">
                      <GridSubItem
                        padding="0 0 10px 0"
                        fontSize="16px"
                        fontStyle="italic"
                      >
                        {t("SUPPLIERS_PLANNING_NOTIFICATION_INFORMATION")}
                      </GridSubItem>
                    </GridRow>
                    <GridRow column="1fr">
                      <RadioItem className="add-edit-planning-notification__radiolabel">
                        <RadioButton
                          value={type}
                          onChange={(e) => onTypeChange(e)}
                          name="planning-notification-type"
                          options={[
                            {
                              label: t(
                                "SUPPLIERS_PLANNING_NOTIFICATION_CHOICE_HAULIERS"
                              ),
                              value: 0,
                            },
                            {
                              label: t(
                                "SUPPLIERS_PLANNING_NOTIFICATION_CHOICE_CENTRES"
                              ),
                              value: 1,
                            },
                          ]}
                        />
                      </RadioItem>
                    </GridRow>
                    <RowHeader>
                      <GridRow column="1fr" gap="0rem">
                        <GridSubItem
                          padding="10px 10px 10px 17px"
                          color="#fff"
                          fontSize="16px"
                          fontWeight="700"
                        >
                          <CustomCheckBox
                            id="select-all"
                            key="select-all"
                            labelText={t(
                              "SUPPLIERS_PLANNING_NOTIFICATION_SELECT_ALL"
                            )}
                            dataId="selectAll"
                            onChange={(e) => {
                              selectAllCompanies(e);
                            }}
                            checked={selectAll}
                          />
                        </GridSubItem>
                      </GridRow>
                    </RowHeader>
                    <GridRow
                      column="1fr"
                      gap="10px"
                      padding="10px"
                      borderRight="2px solid #767676"
                      borderLeft="2px solid #767676"
                      borderBottom="2px solid #767676"
                      borderRadius="10px"
                    >
                      <SolutionOuter>
                        {data?.length > 0 &&
                          data?.map((planning, planningIndex) => {
                            return (
                              <ProductContainer
                                key={
                                  "supplier-planning-serviceitems-solution-" +
                                  planning?.id +
                                  "-" +
                                  planningIndex
                                }
                              >
                                <GridRow column="3% 38% 42% 15%" gap="1%">
                                  <GridSubItem
                                    fontWeight="700"
                                    fontSize="16px"
                                    padding="0px"
                                    color="#0a4986"
                                  >
                                    <CustomCheckBox
                                      id={planning.id}
                                      key={planning.id}
                                      labelText=""
                                      dataId={planning.id}
                                      onChange={(e) => {
                                        onCompanySelect(
                                          e,
                                          parseInt(planning.id, 10)
                                        );
                                      }}
                                      checked={
                                        companySelected.indexOf(
                                          parseInt(planning.id, 10)
                                        ) !== -1
                                      }
                                    />
                                  </GridSubItem>

                                  <GridRow column="1fr" gap="5px">
                                    <GridSubItem
                                      fontWeight="700"
                                      fontSize="16px"
                                      padding="0px"
                                      color="#0a4986"
                                    >
                                      <Anchor
                                        className="add-edit-planning-notification__link"
                                        url={`/company/view/${planning.id}/general-info`}
                                        rel="noreferrer"
                                        target="_blank"
                                      >
                                        {planning?.name}
                                      </Anchor>
                                    </GridSubItem>
                                    <GridSubItem
                                      fontWeight="400"
                                      fontSize="14px"
                                      color="#474747"
                                      fontStyle="italic"
                                      padding="0px"
                                    >
                                      {t("SUPPLIER_PLANNED_ON")}{" "}
                                      {planning?.sitesCount} {t("SITES")}
                                    </GridSubItem>
                                  </GridRow>
                                  <GridRow column="auto 1fr" gap="5px">
                                    <GridSubItem
                                      fontWeight="400"
                                      fontSize="14px"
                                      color="#474747"
                                      fontStyle="italic"
                                      padding="0px"
                                    >
                                      <Image
                                        src={
                                          planning?.contacts?.length > 0
                                            ? contactBlueIcon
                                            : contactRedIcon
                                        }
                                        alt="planning contacts"
                                        styles={{
                                          width: "25px",
                                          height: "25px",
                                          marginTop: "-5px",
                                        }}
                                      />
                                    </GridSubItem>
                                    <GridRow column="1fr" gap="0px">
                                      {planning?.contacts?.length === 0 && (
                                        <GridSubItem
                                          fontWeight="400"
                                          fontSize="14px"
                                          color="#e83f1e"
                                          fontStyle="normal"
                                          padding="0px"
                                        >
                                          {t(
                                            "SUPPLIERS_PLANNING_NOTIFICATION_NO_CONTACT"
                                          )}
                                        </GridSubItem>
                                      )}
                                      {planning?.contacts?.length > 0 &&
                                        planning?.contacts?.map(
                                          (contact, contactIndex) => {
                                            return (
                                              <GridRow
                                                column="auto 1fr"
                                                gap="5px"
                                                key={
                                                  "supplier-planning-serviceitems-solution-contact" +
                                                  planning?.id +
                                                  "-" +
                                                  planningIndex +
                                                  "-" +
                                                  contactIndex
                                                }
                                              >
                                                <GridSubItem
                                                  color={
                                                    contact.email
                                                      ? "#004c7f"
                                                      : "#e83f1e"
                                                  }
                                                >
                                                  {contact.name}
                                                </GridSubItem>
                                                <GridSubItem
                                                  color={
                                                    contact.email
                                                      ? "#767676"
                                                      : "#e83f1e"
                                                  }
                                                >
                                                  (
                                                  {contact.email
                                                    ? contact.email
                                                    : t(
                                                        "SUPPLIERS_PLANNING_NOTIFICATION_CONTACT_NO_EMAIL"
                                                      )}
                                                  )
                                                </GridSubItem>
                                              </GridRow>
                                            );
                                          }
                                        )}
                                    </GridRow>
                                  </GridRow>
                                  <GridRow column="1fr" gap="0px">
                                    <GridSubItem
                                      alignContent="end"
                                      textAlign="end"
                                      fontSize="12px"
                                      padding="0px 5px 0px 0px"
                                      color={
                                        planning.lastNotification
                                          ? "#32A728"
                                          : "#767676"
                                      }
                                    >
                                      {!planning.lastNotification &&
                                        t(
                                          "SUPPLIERS_PLANNING_NOTIFICATION_NOT_SENT"
                                        )}
                                      {planning.lastNotification && (
                                        <>
                                          <Image
                                            src={sentIcon}
                                            alt="sent notification"
                                          />
                                          {getDateFormat(
                                            planning.lastNotification,
                                            "YYYY-MM-DD HH:mm:ss",
                                            "HH:mm"
                                          )}
                                        </>
                                      )}
                                    </GridSubItem>
                                  </GridRow>
                                </GridRow>
                              </ProductContainer>
                            );
                          })}
                      </SolutionOuter>
                    </GridRow>
                    <GridRow column="1fr">
                      <GridSubItem color="#004C7F" padding="5px 0px">
                        {t("SUPPLIERS_PLANNING_NOTIFICATION_INDICATOR_1")}{" "}
                        {selectedContact} / {totalContact}{" "}
                        {t("SUPPLIERS_PLANNING_NOTIFICATION_INDICATOR_2")}{" "}
                        {companySelected?.length} / {data?.length}{" "}
                        {t("SUPPLIERS_PLANNING_NOTIFICATION_INDICATOR_3")}
                      </GridSubItem>
                    </GridRow>
                    {noContact > 0 && (
                      <GridRow column="1fr" padding="10px 0px">
                        <ErrorDiv>
                          <GridRow column="auto 1fr">
                            <Image
                              src={warningIcon}
                              alt="warning"
                              styles={{ width: "25px", alignSelf: "center" }}
                            />{" "}
                            <RequiredMessage>
                              {t("SUPPLIERS_PLANNING_NOTIFICATION_WARNING")}
                            </RequiredMessage>
                          </GridRow>
                        </ErrorDiv>
                      </GridRow>
                    )}
                  </div>
                )}
              </SectionContainer>
            </FlexRow>
          </OuterWrapper>
        </FormBody>
        {formError !== "" && (
          <GridRow column="1fr" padding="10px 20px">
            <GridSubItem color="red">{t(formError)}</GridSubItem>
          </GridRow>
        )}
        <ButtonWrapper>
          {createPlanningAPIError && <div>{createPlanningAPIError}</div>}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              marginRight: "0.32rem",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isPlanningCreating && <ClipLoader size={15} color="#fff" />}
              {!isPlanningCreating && t("SUBMIT")}
            </>
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditPlanningNotification.propTypes = {
  t: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  countryId: PropTypes.number,
};

AddEditPlanningNotification.defaultProps = {
  isEdit: false,
  countryId: null,
};
export default withTranslation()(AddEditPlanningNotification);
